import React, { useState, useEffect } from 'react';
import { devLog } from '../../utils/devLog';
import { Outlet, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { apiUrls } from '@constants/apiurls';
import { routesPath } from 'constants/routesPath';
import { JobHeader } from '../../components/jobHeader/jobHeader';
import jobs from '../../redux/modules/jobs';
import dataService from '../../services/dataService';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yupSchemaService from '../../services/yupSchemaService';
import { JobPostStatus } from '../../components/jobPostStatus/jobPostStatus';
import classNames from 'classnames';
import stylesForms from 'components/formColumns/forms.module.scss';
import styles from 'components/jobForm/jobForm.module.scss';
import stylesJob from '@components/jobIndexCard/JobForm.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { handleJobData } from '../../redux/common/commonSlice';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { fetchAutoCompleteData } from '../../services/autoCompleteService';
import { useWatch } from 'react-hook-form';
import { mergeJobSubmissionValues } from './jobFormMergeHelper';

export const JobContainer = () => {
  devLog('🚀 JobContainer component rendered');
  
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [colors, setColors] = useState([]);
  const [colorsLoaded, setColorsLoaded] = useState(false);

  const [updateJob, jobUpdateData] = jobs.form().useJobupdateMutation();

  const [createJob, createJobData] = jobs.form().useJobcreateMutation();

  const state = useSelector((state) => state.commonReducer.jobData?.state);
  const jobsData = useSelector((state) => state.commonReducer.jobData);

  const {
    data: jobData,
    error: jobError,
    isLoading: jobLoading,
  } = jobs.list().useJobdetailQuery(
    {
      params: { id: params.jobId, include_archived: 1, include_hidden: 1 },
      extendedPath: apiUrls.getJob(),
    },
    { skip: !params?.jobId, refetchOnFocus: true },
  );

  const [initialValues] = useState(() => {
    if (jobData?.data?.job) {
      return dataService.parseFormDataForJobEdit(jobData?.data?.job) || {};
    }
    const saved =
      localStorage.getItem('jobFormValues') ||
      localStorage.getItem('jobFormVlaues');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') {
          return parsed;
        }
      } catch (e) {}
    }
    const storageJobData = localStorage.getItem('jobsData');
    if (storageJobData) {
      try {
        const parsed = JSON.parse(storageJobData);
        if (parsed?.formValues) return parsed.formValues;
        if (parsed?.job) return parsed.job;
      } catch (e) {}
    }
    return {};
  });
  const form = useForm({
    resolver: yupResolver(yupSchemaService.jobCreationSchema()),
    mode: 'all',
    defaultValues: initialValues,
    shouldUnregister: false,
  });

  // Watch specific form fields for real-time updates
  const formColourId = useWatch({ control: form.control, name: 'colour_id' });
  const formAcronym = useWatch({ control: form.control, name: 'acronym' });
  const formIndicatorAcronym = useWatch({ control: form.control, name: 'indicator_acronym' });

  // Restore jobsData in Redux from localStorage on mount if Redux is empty
  useEffect(() => {
    if (!jobsData?.job) {
      const saved = localStorage.getItem('jobsData');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed?.job) {
            dispatch(handleJobData(parsed));
          }
        } catch (e) {}
      }
    }
  }, [jobsData?.job, dispatch]);

  useEffect(() => {
    if (jobData?.data?.job) {
      const jobFormValues = dataService.parseFormDataForJobEdit(jobData.data.job) || {};
      const defaults = {
        publish_date: jobFormValues.publish_date || dayjs().format("DD-MMM-YYYY"),
        expiry_date: jobFormValues.expiry_date || dayjs().add(3, "month").format("DD-MMM-YYYY"),
        ...jobFormValues,
      };
      form.reset(defaults);
      
      // Store job data and state in Redux for child components to access
      dispatch(handleJobData({ 
        job: jobData.data.job,
        state: {
          images: jobData.data.job.job_images || [],
          country_id: jobData.data.job.country_id,
          city: jobData.data.job.city,
        },
        formValues: jobFormValues 
      }));
    } else {
      // When there's no jobData (new job creation), ensure default values are set without clearing existing fields
      const currentValues = form.getValues();
      const saved =
        localStorage.getItem('jobFormValues') ||
        localStorage.getItem('jobFormVlaues');
      let fallbackValues = {};
      if (saved) {
        try {
          fallbackValues = JSON.parse(saved) || {};
        } catch (e) {}
      }
      const storageJob = JSON.parse(localStorage.getItem('jobsData') || '{}');
      const storedJobObj = storageJob?.formValues || storageJob?.job || {};

      form.reset({
        ...storedJobObj,
        ...fallbackValues,
        ...currentValues,
        publish_date: currentValues.publish_date || fallbackValues.publish_date || dayjs().format("DD-MMM-YYYY"),
        expiry_date: currentValues.expiry_date || fallbackValues.expiry_date || dayjs().add(3, "month").format("DD-MMM-YYYY"),
      });
    }
  }, [jobData]);

  useEffect(() => {
    if (jobUpdateData.isSuccess) {
      // Check if the job was a draft (hidden: 1) and now being published (hidden: 0)
      const wasDraft = jobData?.data?.job?.hidden === 1 || jobData?.data?.job?.job_status === 0;
      const updatedData = form.getValues();
      const isNowPublished = !updatedData.hidden || updatedData.job_status === 1;
      
      if (wasDraft && isNowPublished) {
        toast.success('Draft job published successfully!');
      } else if (wasDraft) {
        toast.success('Draft job updated successfully!');
      } else {
        toast.success('Job updated successfully!');
      }
      
      // Update localStorage cache for archived jobs if this job is archived
      if (jobData?.data?.job?.archived === 1 || jobData?.data?.job?.archived === "1" || jobData?.data?.job?.archived === true) {
        const storedArchived = JSON.parse(localStorage.getItem('recentlyArchivedJobs') || '[]');
        
        const updatedArchived = storedArchived.map(archivedJob => {
          if (archivedJob.id === parseInt(params.jobId)) {
            // Get the updated job data from the API response or current job data
            const updatedJobData = jobUpdateData.data?.job || jobData?.data?.job;
            const formValues = form.getValues();
            
            // Merge the stored job with updated values
            const updatedJob = {
              ...archivedJob,
              ...updatedJobData,
              title: formValues.title || updatedJobData?.title || archivedJob.title,
              company: formValues.company || updatedJobData?.company || archivedJob.company,
              description: formValues.description || updatedJobData?.description || archivedJob.description,
              location: formValues.location || updatedJobData?.location || archivedJob.location,
              salary: formValues.salary || updatedJobData?.salary || archivedJob.salary,
              employment_type: formValues.employment_type || updatedJobData?.employment_type || archivedJob.employment_type,
            };
            
            return updatedJob;
          }
          return archivedJob;
        });
        
        localStorage.setItem('recentlyArchivedJobs', JSON.stringify(updatedArchived));
        
        // Force a refresh of the jobs list by dispatching an event
        window.dispatchEvent(new CustomEvent('archivedJobsUpdated'));
      }
      
      navigate(routesPath.jobs, { replace: false });
    }
    if (jobUpdateData.isError) {
      toast.error('Failed To Update Job!');
    }
  }, [jobUpdateData, jobData, params.jobId, form]);

  useEffect(() => {
    if (createJobData?.isSuccess) {
      // Check if it was saved as draft or published
      const formValues = form.getValues();
      const wasDraft = createJobData?.originalArgs?.body?.hidden === 1;
      
      // Try multiple ways to extract the created job ID from the response
      const createdJobId = createJobData?.data?.data?.id || 
                          createJobData?.data?.id || 
                          createJobData?.data?.data?.job?.id ||
                          createJobData?.data?.job?.id ||
                          createJobData?.data?.results?.id;
                          
      const createdJob = createJobData?.data?.data?.job || 
                        createJobData?.data?.job || 
                        createJobData?.data?.data ||
                        createJobData?.data?.results;
      
      console.log('🎯 Job Creation Success:', { wasDraft, createdJobId, hidden: createJobData?.originalArgs?.body?.hidden });
      console.log('📦 Full API Response:', createJobData?.data);
      
      if (wasDraft) {
        // Track this job as drafted in localStorage
        try {
          let draftedJobs = [];
          const storedDrafted = localStorage.getItem('recentlyDraftedJobs');
          if (storedDrafted) {
            draftedJobs = JSON.parse(storedDrafted);
          }
          
          // Add to drafted jobs list if not already there
          if (createdJobId && !draftedJobs.some(job => String(job.id) === String(createdJobId))) {
            draftedJobs.push({
              id: createdJobId,
              title: formValues.title,
              created: new Date().toISOString().split('T')[0],
              hidden: 1,
              job_status: 0,
              status: 0,
              ...createdJob
            });
            localStorage.setItem('recentlyDraftedJobs', JSON.stringify(draftedJobs));
            console.log('✅ Draft job stored in localStorage:', { id: createdJobId, hidden: 1, job_status: 0 });
          } else if (!createdJobId) {
            console.warn('⚠️ No job ID returned from API, cannot track draft');
          }
        } catch (e) {
          console.error('Failed to store drafted job:', e);
        }
        
        toast.success('Job saved as draft successfully!');
        
        // Dispatch event to switch to draft view
        const event = new CustomEvent('switchToDraftView', { detail: { jobId: createdJobId } });
        window.dispatchEvent(event);
        
        // Also dispatch event to update drafted jobs list in JobIndexList
        const draftedEvent = new CustomEvent('draftedJobsUpdated', { detail: { jobId: createdJobId } });
        window.dispatchEvent(draftedEvent);
      } else {
        toast.success('Job published successfully!');
      }
      
      localStorage.removeItem('jobsData');
      localStorage.removeItem('jobFormValues');
      localStorage.removeItem('jobFormVlaues');
      localStorage.removeItem('jobFormParsedVlaues');
      
      // Reload jobs list after a delay
      setTimeout(() => {
        navigate(routesPath.jobs, { replace: false });
      }, 500);
    }
    if (createJobData?.error) {
      const errorMessage = createJobData?.error?.data?.message || 
                          createJobData?.error?.message || 
                          'Failed to save job. Please check all required fields.';
      toast.error(errorMessage);
      console.error('Create job error:', createJobData?.error);
    }
  }, [createJobData]);

  // Fetch colors
  const getColors = async () => {
    try {
      const response = await fetchAutoCompleteData(null, apiUrls.getColors());
      setColors(response);
      setColorsLoaded(true);
    } catch (err) {
      // Development-only error logging
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch colors:', err);
      }
      setColorsLoaded(true); // Set to true even on error to prevent infinite loading
    }
  };

  useEffect(() => {
    getColors();
  }, []);

  useEffect(() => {
    return () => {
      localStorage.removeItem('jobsData');
      localStorage.removeItem('jobFormValues');
      localStorage.removeItem('jobFormVlaues');
      localStorage.removeItem('jobFormParsedVlaues');
    };
  }, []);

  const submitForm = (values, saveAsDraft = false) => {
    // If submitting/publishing, ensure we merge stored form values and job data
    // in case values are incomplete (e.g. on /job/publish where step 1 inputs were unmounted)
    const storageJobData = JSON.parse(localStorage.getItem('jobsData') || '{}');
    const storageFormValues = JSON.parse(
      localStorage.getItem('jobFormValues') || localStorage.getItem('jobFormVlaues') || '{}'
    );
    values = mergeJobSubmissionValues({
      values,
      reduxJobsData: jobsData,
      localStorageJobsData: storageJobData,
      localStorageFormValues: storageFormValues,
    });

    console.log('📋 submitForm called with:', { saveAsDraft, valuesKeys: Object.keys(values), description: values.description?.substring(0, 50) });
    
    // Auto-fill missing publish_date and expiry_date values
    if (!values.publish_date) {
      values.publish_date = dayjs().format('DD-MMM-YYYY');
    }
    if (!values.expiry_date) {
      values.expiry_date = dayjs().add(3, 'month').format('DD-MMM-YYYY');
    }

    // Validate required fields before submission
    if (!values.title || values.title.trim() === '') {
      toast.error('Job title is required');
      return;
    }
    
    if (!values.client_id) {
      toast.error('Client is required');
      return;
    }

    // For drafts, allow empty description (user can save and edit later)
    // For publishing, require description
    if (!saveAsDraft && (!values.description || values.description.trim() === '')) {
      toast.error('Job description is required to publish');
      return;
    }
    
    console.log('✅ All validations passed, proceeding with', saveAsDraft ? 'DRAFT' : 'PUBLISH');

    // Set draft/publish flags BEFORE parsing so they don't get overridden
    if (saveAsDraft) {
      values.job_status = 0;
      values.hidden = 1;
      values.archived = 0;
    } else {
      values.job_status = 1;
      values.hidden = 0;
      values.archived = 0;
    }

    let parsedValues = dataService.parseDataForJobCreationApi(values);
    
    // Ensure draft flags are set correctly after parsing
    if (saveAsDraft) {
      parsedValues.job_status = 0;
      parsedValues.status = 0;
      parsedValues.hidden = 1;
      parsedValues.archived = 0;
      console.log('📝 Saving as DRAFT with values:', { job_status: parsedValues.job_status, hidden: parsedValues.hidden, status: parsedValues.status });
    } else {
      // When publishing (not draft), ensure it's active and visible
      parsedValues.job_status = 1;
      parsedValues.hidden = 0;
      parsedValues.archived = 0;
      console.log('📤 Publishing job with values:', { job_status: parsedValues.job_status, hidden: parsedValues.hidden });
    }
  
    if (
      new Date(parsedValues?.publish_date).getTime() >=
      new Date(parsedValues?.expiry_date).getTime()
    ) {
      toast.error('Publish time should be less than expiry time');
      return;
    }

    // Handle job_images safely - check if state and images exist
    parsedValues.job_images = state?.images?.map((img) => {
      if (img) {
        return { 
          url: img.url || img.image,
          is_cover: img.is_cover || false
        };
      }
    }).filter(Boolean) || [];

    parsedValues.cover_image = Array.isArray(state?.images)
      ? state.images.find((img) => img.is_cover === 1 || img.is_cover === true)?.image
      : undefined;
    const storageJob = JSON.parse(localStorage.getItem('jobsData'))?.job;
    localStorage.setItem(
      'jobsData',
      JSON.stringify({ state: state || {}, job: parsedValues, formValues: values }),
    );

    if (parsedValues.job_images?.length === 0 && storageJob?.job_images?.length > 0) {
      parsedValues.job_images = storageJob.job_images;
    }

    if (storageJob?.cover_image && !parsedValues.cover_image) {
      parsedValues.cover_image = storageJob.cover_image;
    }

    if (params.jobId) {
      parsedValues.id = parseInt(params.jobId);
      
      // Set draft flags if saving as draft
      if (saveAsDraft) {
        parsedValues.job_status = 0;
        parsedValues.status = 0;
        parsedValues.hidden = 1;
        parsedValues.archived = 0;
      } else {
        // When publishing, ensure active status
        parsedValues.job_status = 1;
        parsedValues.hidden = 0;
        parsedValues.archived = 0;
      }
      
      dispatch(handleJobData({ state: state || {}, job: parsedValues, formValues: values }));

      updateJob(parsedValues);
    } else {
      !window.location.pathname.includes('publish') &&
        localStorage.setItem('jobFormVlaues', JSON.stringify(values));
      !window.location.pathname.includes('publish') &&
        localStorage.setItem('jobFormValues', JSON.stringify(values));
      dispatch(handleJobData({ state: state || {}, job: parsedValues, formValues: values }));
      
      if (saveAsDraft) {
        // Save as draft immediately
        console.log('📤 Calling createJob API with draft data...');
        console.log('📊 Full parsed values being sent:', parsedValues);
        createJob({ body: parsedValues });
      } else if (window.location.pathname.includes('publish')) {
        createJob({ body: parsedValues });
      } else {
        navigate(routesPath.publishJob());
      }
    }
  };  // Development-only form debug logging
  devLog('🎨 Form Color Debug:', {
    formColourId,
    formAcronym,
    formIndicatorAcronym,
    originalJobColorId: jobData?.data?.job?.colour_id,
    colorsLoaded,
    colorsCount: colors?.length
  });
  
  return (
    <>
      <JobHeader
        client={jobData?.data?.job}
        job={{
          ...jobData?.data?.job,
          // Override with current form values for real-time updates
          colour_id: formColourId || jobData?.data?.job?.colour_id,
          indicator_acronym: formIndicatorAcronym || jobData?.data?.job?.indicator_acronym,
          acronym: formAcronym || jobData?.data?.job?.acronym,
        }}
        colors={colorsLoaded ? colors : []}
        // projects={projects}
        newEntry={!params?.jobId}
        onCancel={routesPath.dashboard}
        // active="settings"
        // onEdit={(client) => push(routes.editClient(client.id))}
        // onRemove={(client) =>
        //   requireConfirmation([destroy(client.uri), pushAction(routes.dashboard)])
        // }
      />

      <div className={classNames(styles.page, { [stylesJob.applicantsPage]: params?.jobId })}>
        <div
          className={
            (classNames(styles.matchCompCardContent), 'd-flex align-self-center flex-column')
          }
        >
          <div className="container-fluid p-0">
            {jobLoading ? (
              <></>
            ) : (
              <FormProvider {...form}>
                <form
                  onSubmit={form.handleSubmit(submitForm)}
                  className={classNames('form', stylesForms.form)}
                >
                  <div className="row">
                    <div className="col-lg-8">
                      <Outlet context={{ form }} />
                    </div>

                    <div className="col-lg-4">
                      <div className={classNames('ps-0', 'pe-0', 'pe-lg-3 pt-5 mt-4')}>
                        <JobPostStatus
                          showCreate={true}
                          isEditing={params.jobId}
                          loading={jobLoading}
                          form={form}
                          jobData={jobData?.data?.job || jobsData?.job}
                          submitForm={submitForm}
                        />
                      </div>
                    </div>
                  </div>
                </form>
              </FormProvider>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export const useJobConatiner = () => {
  return useOutletContext();
};
